import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Account security, settings, and email verification routes. Public routes
 * (email verification flows) are exported separately as ACCOUNT_MANAGEMENT_PUBLIC_ROUTES
 * so they can be placed at root level in app.routes.ts outside the auth guard.
 */
export const ACCOUNT_MANAGEMENT_ROUTES: Routes = [
    {
        path: 'accountSecurity',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'accountSecurity',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/account-security/account-security.component').then(m => m.AccountSecurityComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'accountSettings',
        loadComponent: () => import('src/components/account-settings/account-settings.component').then(m => m.AccountSettingsComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'accountSettings',
            requiresPermission: true
        } satisfies RouteData
    },

    {
        path: 'accountProfile',
        loadComponent: () => import('src/components/entity-record/account/account.component').then(m => m.AccountComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'maintainCardProfile',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'maintainCardProfile',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/maintain-card-profile/maintain-card-profile.component').then(m => m.MaintainCardProfileComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    // Note: Unauthenticated routes (resetPasswordInactivity, activateEmailAddressChange, verifyBackupEmailAddress)
    // have been moved to ACCOUNT_MANAGEMENT_PUBLIC_ROUTES and are imported at root level in app.routes.ts
];

/**
 * Public (Unauthenticated) Account Management Routes
 *
 * These routes must be at root level in app.routes.ts, not nested in protected parent.
 * Includes password reset and email verification flows that don't require authentication.
 */
export const ACCOUNT_MANAGEMENT_PUBLIC_ROUTES: Routes = [
    // Password reset due to inactivity
    {
        path: 'resetPasswordInactivity',
        loadComponent: () => import('src/components/reset-password-inactivity/reset-password-inactivity.component').then(m => m.ResetPasswordInactivityComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'resetPasswordInactivity',
            unauthenticated: true
        } satisfies RouteData
    },

    // Activate email address change via token
    {
        path: 'activateEmailAddressChange',
        loadComponent: () => import('src/components/activate-email-address-change/activate-email-address-change.component').then(m => m.ActivateEmailAddressChangeComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'activateEmailAddressChange',
            unauthenticated: true
        } satisfies RouteData
    },

    // Verify backup email address
    {
        path: 'verifyBackupEmailAddress',
        loadComponent: () => import('src/components/account-security/verify-account-backup-email/verify-account-backup-email.component').then(m => m.VerifyAccountBackupEmailComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'verifyBackupEmailAddress',
            unauthenticated: true
        } satisfies RouteData
    }
];
